import React, { useMemo } from "react";
import clsx from "clsx";
import { Switch } from "react-router-dom";
import { PrivateRoute, Box } from "@common/components/";
import InventoryHeader from "./InventoryHeader";
import InventoryNavMenu from "./InventoryNavMenu";
import InventoryNavUserInfo from "./InventoryNavUserInfo";
import { InventoryNav } from "@clubInventory/components/";
import inventorySubRouter from "@clubInventory/router/subRouters";
import { isEmpty, isEqual } from "@util/utils";
import { getMenusData } from "@redux/menus/selector";
import { useSelector } from "react-redux";
import useEnvName from "@util/hook/useEnvName";
import { NotFoundPage } from "@error/";

import '@scss/clubInventory/index.scss';
import '@curves/ui-base/src/styles/index.scss';

const collectFrontKeys = (menus = []) => {
	return menus.reduce((acc, menu) => {
		if (!menu) return acc;
		const next = [...acc];
		if (menu.frontKey) next.push(menu.frontKey);
		if (Array.isArray(menu.children) && menu.children.length > 0) {
			next.push(...collectFrontKeys(menu.children));
		}
		return next;
	}, []);
};

/**
 * 店鋪庫存管理系統主 Layout
 * 提供獨立的導覽列和內容區域
 */
const InventoryLayout = () => {
	const menuData = useSelector(getMenusData);
	const hostname = window.location.hostname;
	const envName = useEnvName();
	const envClass = envName && `station-${envName}`;

	// 後端 menu 與前端 route 以 frontKey 交集核對，可渲染 route 以交集為準
	const renderableFrontKeySet = useMemo(() => {
		const menuFrontKeys = new Set(collectFrontKeys(menuData?.oriMenuData || []));
		return new Set(inventorySubRouter.map(({ frontKey }) => frontKey).filter(frontKey => menuFrontKeys.has(frontKey)));
	}, [menuData]);

	// 渲染路由，統一處理權限檢查和開發測試邏輯
	const renderRoutes = useMemo(() => {
		if (isEmpty(inventorySubRouter)) return [];

		return inventorySubRouter
			.map((route, index) => {
				const { frontKey, exact, path: routePath, as: Component, ...others } = route;
				if (!renderableFrontKeySet.has(frontKey)) return null;

				// 檢查權限
				let isExist = menuData?.menulist?.find(({ frontKey: _key }) => isEqual(frontKey, _key));

				// 開發測試期間：localhost 且 frontKey 符合 CXX99 開頭
				if (/^CXX99[\d]+$/g.test(frontKey) && isEqual(hostname, "localhost")) {
					isExist = {}; // 專門給正在開發且測試的頁面使用
				}

				// 如果沒有權限，不渲染該路由
				if (!isExist) return null;

				const defaultProperty = {
					exact,
					path: routePath,
					as: Component,
					...others,
				};

				return (
					<PrivateRoute
						key={`${frontKey}_${index}`}
						{...defaultProperty}
						isAuth={isExist.isAuth}
					/>
				);
			})
			.filter(Boolean); // 過濾掉 null
	}, [menuData, hostname, renderableFrontKeySet]);

	return (
		<InventoryNav defaultOpen={true} animationDuration={300} navWidth={240}>
			<Box className="club-inventory">
				<InventoryHeader className={envClass} />
				<Box className="club-inventory-container">
					<InventoryNav.Panel className={clsx("club-inventory-nav", envClass)}>
						<Box className="club-inventory-nav-wrapper">
							<InventoryNavMenu />
							<InventoryNavUserInfo />
						</Box>
					</InventoryNav.Panel>
					<InventoryNav.Content>
						<Switch>
							{renderRoutes}
							<PrivateRoute path="*" as={NotFoundPage} />
						</Switch>
					</InventoryNav.Content>
				</Box>
			</Box>
		</InventoryNav>
	);
};

export default InventoryLayout;
